import { defineStore } from "pinia";
import { computed } from "vue";
import { t } from "@/i18n";
import {
    BookIcon,
    BusinessHandshakeIcon,
    PlayerIcon,
    FaqIcon,
    LayoutListIcon,
    LayoutBoardIcon,
    LayoutTableIcon,
    LayoutColumnsIcon,
    LayoutTtitleAsideIcon,
    LayoutSliderIcon,
} from "@easy/ui/icons";
import { makeLogger } from "@/shared/consola";
import PreviewList from "@/apps/faq/project/onboarding/svg-preview/PreviewList.vue";
import PreviewBoard from "@/apps/faq/project/onboarding/svg-preview/PreviewBoard.vue";
import PreviewTable from "@/apps/faq/project/onboarding/svg-preview/PreviewTable.vue";
import PreviewColumns from "@/apps/faq/project/onboarding/svg-preview/PreviewColumns.vue";
import PreviewAside from "@/apps/faq/project/onboarding/svg-preview/PreviewAside.vue";
import PreviewSlider from "@/apps/faq/project/onboarding/svg-preview/PreviewSlider.vue";
import { useOnboarding } from "@/shared/projects/onboarding/useOnboarding";
import { useActions, useMutations, useState } from "@/store/vuex-composition-mappers";
import { mapThemeToFaqColors } from "@/shared/projects/prepare-theme";

const STORE_NAME = "faq-creator";
const logger = makeLogger("🍍" + STORE_NAME);

export const useFaqCreatorStore = defineStore(STORE_NAME, () => {
    logger.trace("👋hello");

    const { faq, sections } = useState("faq", ["faq", "sections"]);
    const { addQuestion } = useMutations("faq", ["addQuestion"]);
    const { saveSnapshot } = useActions("faq/projects", ["saveSnapshot"]);
    const { showAlert } = useMutations(["showAlert"]);

    const {
        isCreatorShown,
        onboardState,
        previewCssVars,
        autoOpenCreator,
        toggleCreatorShown,
        updateProductInfo,
        validate,
    } = useOnboarding({
        // autoOpenRules: (numberOfQuestions) => numberOfQuestions === 0,
        initialValues: {
            layout: "list",
            hidePrompt: true, // for now (EAS-2196)
        },
    });

    const templates = computed(() => [
        {
            value: t("faq.templates.empty"),
            id: "empty",
        },
        {
            value: t("faq.templates.video"),
            icon: PlayerIcon,
            id: "video",
        },
        {
            value: "E-book",
            icon: BookIcon,
            id: "ebook",
        },
        {
            value: t("faq.templates.consulting"),
            icon: BusinessHandshakeIcon,
            id: "consulting",
        },
        {
            value: "Easyfaq",
            icon: FaqIcon,
            id: "faqs",
        },
    ]);

    const layouts = computed(() => [
        {
            value: "list",
            label: t("faq.layout.list"),
            icon: LayoutListIcon,
            preview: PreviewList,
        },
        {
            value: "board",
            label: t("faq.layout.board"),
            icon: LayoutBoardIcon,
            preview: PreviewBoard,
        },
        {
            value: "table",
            label: t("faq.layout.table"),
            icon: LayoutTableIcon,
            preview: PreviewTable,
        },
        {
            value: "columns",
            label: t("faq.layout.columns"),
            icon: LayoutColumnsIcon,
            preview: PreviewColumns,
        },
        {
            value: "aside",
            label: t("faq.layout.aside"),
            icon: LayoutTtitleAsideIcon,
            preview: PreviewAside,
        },
        {
            value: "slider",
            label: t("faq.layout.slider"),
            info: t("faq.layout.slider_info"),
            icon: LayoutSliderIcon,
            preview: PreviewSlider,
        },
    ]);

    const previewComponent = computed(
        () => layouts.value.find((l) => l.value === onboardState.layout)?.preview
    );

    /*
    const mapFaqColorsToCssVars = (faq) => ({
        // "--et-colors-brand": faq.,
        "--et-colors-background": faq.faqColor,
        "--et-colors-text": faq.titleColor,
        "--et-colors-card-background": faq.backColor,
        "--et-colors-card-text": faq.questionColor,
        "--et-colors-border": faq.borderColor,
    });
    */

    return {
        onboardState,
        isCreatorShown,
        templates,
        layouts,
        previewCssVars,
        previewComponent,
        links: computed(() => ({
            video: "",
            docs: "",
        })),
        autoOpenCreator,
        onboardingAction(resolveResponse) {
            return async () => {
                if (!validate()) {
                    return;
                }
                let fireResolver = resolveResponse;
                if (onboardState.mode === "by-product" && onboardState.productSelectLater) {
                    fireResolver = async () => ({
                        result: {
                            title: t("faq.sample.title"),
                            subtitle: t("faq.sample.subtitle"),
                            questions: [{ question: t("faq.question"), answer: t("faq.answer") }],
                        },
                    });
                }
                try {
                    onboardState.isLoading = true;
                    const { result } = await fireResolver();
                    const { title = "", subtitle = "", questions } = result;
                    const { theme, layout } = onboardState;
                    const [firstSection] = sections.value;

                    logger.debug("about to fill form resolveResponse()", { result, theme, layout });
                    for (const question of questions || []) {
                        addQuestion({ ...question, sectionId: firstSection.id });
                    }
                    faq.value.title = title;
                    faq.value.subtitle = subtitle;
                    faq.value.layout = layout;
                    for (const [key, value] of Object.entries(mapThemeToFaqColors(theme))) {
                        faq.value[key] = value;
                    }
                    showAlert({
                        type: "success",
                        title: t("faq.import.sample_success"),
                    });
                    await saveSnapshot();
                    toggleCreatorShown(false);
                } catch (e) {
                    logger.error(e);
                } finally {
                    onboardState.isLoading = false;
                }
            };
        },
        toggleCreatorShown,
        updateProductInfo,
        validate,
    };
});
